import {CircularProgress, Typography, Dialog, Table, TableHead, TableBody, Toolbar, IconButton, AppBar, TableRow, TableCell, Grid, Card, CardContent} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from "react-i18next";
import { grey } from '@mui/material/colors';
import React, {useState} from 'react';
import {observer} from 'mobx-react';
import {getShortSpecName, getCityName, monthNames} from './Trans.js';
import {useGlobalStorage} from './mobx/GlobalStorage.js';
import {getMidnightEpoch, dateStr} from './Util.js';
import {COMM_OK} from './WsR2.js';

const DayRepCell = (props) => (
	<TableCell align={'center'} sx={{borderWidth: 0, whiteSpace: 'nowrap', padding: '2px'}}>
		{props.children}
	</TableCell>
);

export const DayStatsCardTemplate = observer((props) => {
	return (			
		<Card raised={true} style={{margin: 5, marginTop: 10, backgroundColor: grey[100]}}>
			<CardContent sx={{padding: 0}}>
				<Table size='small' style={{padding: 0}}>
					<TableHead>
						<TableRow>
							<TableCell colSpan={3} align={'center'} sx={{borderWidth: 0, whiteSpace: 'nowrap', padding: '3px'}}>
								{props.header}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{props.children}
					</TableBody>
				</Table>
			</CardContent>
		</Card>
	);
});

export const TripsCard = observer((props) => {
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');

	if (!gStorage.trips.stats || !gStorage.trips.stats.length)
		return null;

	function tripContent() {
		let ret = [];
		
		gStorage.trips.stats.forEach((trip, ind) => {
			const _trip = gStorage.lists.triplist.list.find((t) => t.id === trip.trip);
			let num = 0, n_spec = 0;

			if (_trip) {
				trip.taxons.forEach((t, ind) => {
					num += t.num;

					const _spec = gStorage.lists.speclist.list.find((spec) => spec.id === t.id);
					if (_spec && _spec.stats)
						n_spec++;
				});

				ret.push(
					<TableRow key={ind}> 
						<TableCell align={'center'} sx={{borderWidth: 0, padding: '3px'}}>
							{_trip.name + ': ' + trip.n_obs + ' - '  + n_spec + ' - ' + num}
						</TableCell>
					</TableRow>
				)
			}
		});

		return ret;
	}

	return (
		<DayStatsCardTemplate header={t('dayrep.trips')}>
			{tripContent()}
		</DayStatsCardTemplate>
	);
});

export const DayStatsCard = (props) => {
	const {t} = useTranslation('obs');

	if (!props.stats || !props.stats.stats)
		return null;

	if (!props.stats.stats.num_obs) {
		return (
				<Table size='small' style={{padding: 0}}>
					<TableHead>
						<TableRow>
							<TableCell colSpan={3} align={'center'} sx={{borderWidth: 0, whiteSpace: 'nowrap', padding: '3px', paddingTop: '32px'}}>
								{t('dayrep.no_observations')}
							</TableCell>
						</TableRow>
					</TableHead>
				</Table>
		)
	}

	return (
		<DayStatsCardTemplate header={t('dayrep.obs_title')}>
			<TableRow>
				<TableCell align={'left'} sx={{borderWidth: 0, whiteSpace: 'nowrap', padding: '3px', paddingLeft: '16px'}}>
					{t('dayrep.observations')}
				</TableCell>
				<DayRepCell>
					{props.stats.stats.num_obs}
				</DayRepCell>
			</TableRow>
			<TableRow>
				<TableCell align={'left'} sx={{borderWidth: 0, whiteSpace: 'nowrap', padding: '3px', paddingLeft: '16px'}}>
					{t('dayrep.num_species')}
				</TableCell>
				<DayRepCell>
					{props.stats.stats.num_spec}
				</DayRepCell>
			</TableRow>
			<TableRow>
				<TableCell align={'left'} sx={{borderWidth: 0, whiteSpace: 'nowrap', padding: '3px', paddingLeft: '16px'}}>
					{t('dayrep.num')}
				</TableCell>
				<DayRepCell>
					{props.stats.stats.num_birds}
				</DayRepCell>
			</TableRow>
		</DayStatsCardTemplate>
	);
};

function DayRepRow(elem, ind) {
	const gStorage = useGlobalStorage();
	let city_name = '';

	const spec = gStorage.lists.speclist.list.find((spec) => spec.id === elem.spec);
	const spec_name = getShortSpecName(spec, gStorage.conn.user);

	if (elem.city) {
		const city = gStorage.lists.citylist.list.find((city) => city.id === elem.city);
		city_name = getCityName(city, gStorage.conn.user);
	} 

	return (
		<TableRow key={ind}>
			<DayRepCell>
				{elem.city ? spec_name + ', ' + city_name : spec_name}
			</DayRepCell>
		</TableRow>
	)
}

export const DayLiferCard = (props) => {
	const {t} = useTranslation('obs');

	if (!props.stats || !props.stats.lifers)
		return null;

	return (
		<DayStatsCardTemplate header={t('dayrep.lifers') + ' (' + props.stats.lifers.length + ')'}>
			{props.stats.lifers.map((lifer, ind) => {
				return DayRepRow(lifer, ind);
			})}
		</DayStatsCardTemplate>
	);
};

export const DayYearCard = (props) => {
	const {t} = useTranslation('obs');

	if (!props.stats || !props.stats.year)
		return null;

	return (
		<DayStatsCardTemplate header={t('dayrep.year') + ' (' + props.stats.year.length + ')'}>
			{props.stats.year.map((year, ind) => {
				return DayRepRow(year, ind);
			})}
		</DayStatsCardTemplate>
	);
};

export const DayCityCard = (props) => {
	const {t} = useTranslation('obs');

	if (!props.stats || !props.stats.cities)
		return null;

	return (
		<DayStatsCardTemplate header={t('dayrep.cities') + ' (' + props.stats.cities.length + ')'}>
			{props.stats.cities.map((city, ind) => {
				return DayRepRow(city, ind);
			})}
		</DayStatsCardTemplate>
	);
};

export const DayMonthCard = (props) => {
	const {t} = useTranslation('obs');
	const ts = new Date();

	if (!props.stats || !props.stats.months)
		return null;

	ts.setTime(props.stats.epoch * 1000);

	return (
		<DayStatsCardTemplate header={t(monthNames[ts.getMonth()]) + ' (' + props.stats.months.length + ')'}>
			{props.stats.months.map((month, ind) => {
				return DayRepRow(month, ind);
			})}
		</DayStatsCardTemplate>
	);
};

export const DayDateCard = (props) => {
	const ts = new Date();

	if (!props.stats || !props.stats.date)
		return null;

	ts.setTime(props.stats.epoch * 1000);

	return (
		<DayStatsCardTemplate header={/*t('dayrep.date') + ': ' +*/ dateStr(props.stats.epoch) + ' (' + props.stats.date.length + ')'}>
			{props.stats.date.map((date, ind) => {
				return DayRepRow(date, ind);
			})}
		</DayStatsCardTemplate>
	);
};

const DayReport = observer((props) => {
	const [loading, setLoading] = useState(false);
	const gStorage = useGlobalStorage();
	const {t} = useTranslation('obs');
	const {isOpen, close} = props;

	if (!isOpen)
		return null;

	function dayRepRxWs(status, msg, priv) {
		setLoading(false);

		if (status !== COMM_OK) {
			console.error('mainRxWs: error ', status);
			return -1;
		}

		// console.log('msg', msg.data.dayreport);

		msg.data.dayreport.epoch = priv;
		gStorage.dayrep.setStats(msg.data.dayreport);
	}

	function loadDayRep() {
		const midnight = getMidnightEpoch(new Date());
		const ts = new Date();
		ts.setTime(midnight * 1000);
		const month = ts.getMonth();

		const msg = {
			sess_id: gStorage.conn.sess_id,
			cmd: 'day',
			op: 'report',
			data: {
				user: gStorage.conn.user.id,
				country: 0,
				epoch: midnight,
				month: month
			}
		}

		gStorage.conn.ws.send(msg, dayRepRxWs, msg.data.epoch);
		setLoading(true);
	}

	if (!gStorage.dayrep.stats) {
		if (!loading)
			loadDayRep();

		return null;
	}

	return (
		<Dialog onClose={close} aria-labelledby="simple-dialog-title" open={isOpen ? true : false} PaperProps={{sx: {margin: 0,  minHeight:150, padding: 0, borderRadius: 2}}}>
			<Grid container direction="row" justifyContent="center" alignItems="center" spacing={0} style={{width:316}}>
				<AppBar position="sticky">
					<Toolbar variant="dense">
						<Typography variant="h6" style={{flexGrow: 1, textAlign: 'center'}}>{t('dayrep.dayrep')}</Typography>
						<IconButton edge="start" color="inherit" onClick={close} aria-label="close">
							<CloseIcon />
						</IconButton>
					</Toolbar>
				</AppBar>

				{ loading ? 
					<CircularProgress sx={{mt:3}}/>
				:
					<Grid size={{xs: 12}} align={'center'}>
						<DayStatsCard stats={gStorage.dayrep.stats} />
						<TripsCard />
						<DayLiferCard stats={gStorage.dayrep.stats} />
						<DayYearCard stats={gStorage.dayrep.stats} />
						<DayMonthCard stats={gStorage.dayrep.stats} />
						<DayCityCard stats={gStorage.dayrep.stats} />
						<DayDateCard stats={gStorage.dayrep.stats} />
					</Grid>	
				}
			</Grid>
		</Dialog>
	)
});

export default DayReport;
